/** @const {string} Browser storage key prefix for a personal dashboard layout. */
export const OPERATIONS_LAYOUT_STORAGE_PREFIX = "operations-dashboard-layout";

/** @const {Array<Object>} Configurable dashboard cards in the default sequence. */
export const DASHBOARD_CARDS = [
  {
    key: "sales",
    label: "Wholesale sales orders",
    group: "kpi",
    permission: "can_view_sales_orders",
  },
  {
    key: "purchases",
    label: "Purchase orders",
    group: "kpi",
    permission: "can_view_purchase_orders",
  },
  {
    key: "receipts",
    label: "Purchase receipts",
    group: "kpi",
    permission: "can_view_receipts",
  },
  {
    key: "logistics",
    label: "Upcoming shipments",
    group: "kpi",
    permission: "can_view_shipments",
  },
  { key: "comingUp", label: "Coming up", group: "calendar" },
  { key: "attention", label: "Needs your attention", group: "panel" },
  {
    key: "shipments",
    label: "Upcoming shipments list",
    group: "panel",
    permission: "can_view_shipments",
  },
  { key: "integrations", label: "Integration statuses", group: "snapshot", snapshot: "integrations" },
  { key: "stock", label: "Stock on hand value", group: "snapshot", snapshot: "stock" },
  {
    key: "purchasesStatus",
    label: "Pending purchase orders",
    group: "snapshot",
    snapshot: "purchases",
  },
  { key: "salesStatus", label: "Pending sales orders", group: "snapshot", snapshot: "sales" },
  {
    key: "paymentsStatus",
    label: "Purchase order payments",
    group: "snapshot",
    snapshot: "payments",
    permission: "can_view_purchase_orders",
  },
];

/** @const {Set<string>} Summary KPI card keys. */
export const KPI_CARD_KEYS = new Set(DASHBOARD_CARDS.filter(card => card.group === "kpi").map(card => card.key));

/** @const {Set<string>} Calendar card keys. */
export const CALENDAR_CARD_KEYS = new Set(
  DASHBOARD_CARDS.filter(card => card.group === "calendar").map(card => card.key),
);

/** @const {Set<string>} Queue table keys. */
export const PANEL_CARD_KEYS = new Set(
  DASHBOARD_CARDS.filter(card => card.group === "panel").map(card => card.key),
);

/** @const {Set<string>} Snapshot panel keys. */
export const SNAPSHOT_CARD_KEYS = new Set(
  DASHBOARD_CARDS.filter(card => card.group === "snapshot").map(card => card.key),
);

/**
 * @param {number|string} userId Authenticated user id.
 * @param {number|string} companyId Company id.
 * @return {string}
 */
export const operationsLayoutStorageKey = (userId, companyId) =>
  `${OPERATIONS_LAYOUT_STORAGE_PREFIX}:${userId || "anon"}:${companyId || "company"}`;

/**
 * Cards the current overview may show, respecting permissions and snapshot presence.
 * @param {Object} data Operations overview payload.
 * @return {Array<Object>}
 */
export const availableDashboardCards = data => {
  if (!data) return [];
  return DASHBOARD_CARDS.filter(card => {
    if (card.permission && !data.permissions?.[card.permission]) return false;
    if (card.key === "comingUp") {
      return Boolean(data.permissions?.can_view_shipments || data.permissions?.can_view_notes);
    }
    if (card.key === "attention") {
      return ["can_view_sales_orders", "can_view_purchase_orders", "can_view_receipts"].some(
        permission => data.permissions?.[permission],
      );
    }
    if (card.snapshot) return Boolean(data.snapshots && card.snapshot in data.snapshots);
    return true;
  });
};

/**
 * Merge a stored preference with the cards available now.
 * @param {Array<string>} availableKeys Cards the user can currently see.
 * @param {?Object} stored Saved sequence and hidden keys.
 * @return {Array<{key: string, visible: boolean}>}
 */
export const resolveDashboardLayout = (availableKeys, stored) => {
  if (!stored) return availableKeys.map(key => ({ key, visible: true }));
  const available = new Set(availableKeys);
  const sequence = Array.isArray(stored.sequence) ? stored.sequence : [];
  const hidden = new Set(Array.isArray(stored.hidden) ? stored.hidden : []);
  const seen = new Set([...sequence, ...hidden]);
  const ordered = [...sequence.filter(key => available.has(key)), ...availableKeys.filter(key => !seen.has(key))];
  return ordered.map(key => ({ key, visible: !hidden.has(key) }));
};

/**
 * @param {number|string} userId Authenticated user id.
 * @param {number|string} companyId Company id.
 * @return {?Object}
 */
export const loadDashboardLayout = (userId, companyId) => {
  try {
    const raw = window.localStorage.getItem(operationsLayoutStorageKey(userId, companyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * @param {number|string} userId Authenticated user id.
 * @param {number|string} companyId Company id.
 * @param {Object} layout Sequence and hidden keys.
 * @return {void}
 */
export const saveDashboardLayout = (userId, companyId, layout) => {
  window.localStorage.setItem(operationsLayoutStorageKey(userId, companyId), JSON.stringify(layout));
};
